import React from 'react';
import { Check, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useI18n } from '@/contexts/I18nContext';

type Props = {
  featureName: string;
  currentPackageCode?: string | null;
  onUpgrade?: () => void;
  availabilityText?: string;
  canUpgrade?: boolean;
  contactAdminText?: string;
};

const normalizePackage = (value?: string | null) => String(value || 'package1').trim().toLowerCase();

const packageLabel = (value: string | null | undefined, t: (key: string, fallback?: string) => string) => {
  const pkg = normalizePackage(value);
  if (pkg === 'package3') return t('package_label_elite', 'Elite (Package 3)');
  if (pkg === 'package2') return t('package_label_pro', 'Pro (Package 2)');
  return t('package_label_starter', 'Starter (Package 1)');
};

const packagePricing = (value: string | null | undefined, t: (key: string, fallback?: string) => string) => {
  const pkg = normalizePackage(value);
  if (pkg === 'package3') return `$299/${t('pkg_month', 'month')}`;
  if (pkg === 'package2') return `$159/${t('pkg_month', 'month')}`;
  return `$69/${t('pkg_month', 'month')}`;
};

const currentPlanHighlights = (value: string | null | undefined, t: (key: string, fallback?: string) => string) => {
  const pkg = normalizePackage(value);
  if (pkg === 'package3') {
    return [
      t('pkg_feature_everything_pro', 'Everything in Pro'),
      t('pkg_feature_250_members', 'Up to 250 active members'),
      t('pkg_feature_multi_admin', 'Advanced roles and permissions (multiple club admins)'),
      t('pkg_feature_ui_branding', 'Advanced UI controls (colors/fonts for club branding)'),
      t('pkg_feature_logo', 'Club logo upload/management'),
      t('pkg_feature_avatar', 'Member profile avatar selection'),
      t('pkg_feature_ai_chat', 'AI chat for match-history insights'),
      t('pkg_feature_dashboard_insights', 'Actionable performance insights on dashboard'),
    ];
  }
  if (pkg === 'package2') {
    return [
      t('pkg_feature_everything_starter', 'Everything in Starter'),
      t('pkg_feature_150_members', 'Up to 150 active members'),
      t('pkg_feature_coach_eval', 'Coach evaluations'),
      t('pkg_feature_equipment', 'Equipment tracking'),
      t('pkg_feature_ml_limited', 'Limited ML-assisted analytics for key performance trends'),
      t('pkg_feature_dashboard_insights', 'Actionable performance insights on dashboard'),
      t('pkg_feature_daily_advanced', 'Daily match reports and advanced leaderboard trends'),
    ];
  }
  return [
    t('pkg_feature_50_members', 'Up to 50 active members'),
    t('pkg_feature_member_mgmt', 'Member management'),
    t('pkg_feature_queue_courts', 'Queue and court assignment'),
    t('pkg_feature_basic_leaderboard', 'Match history and basic leaderboard'),
    t('pkg_feature_basic_dashboard', 'Basic admin dashboard'),
  ];
};

const renderAvailabilityWithLinks = (text: string) => {
  const parts = text.split(/(\(Package 2\)|\(Package 3\)|\(\u0627\u0644\u0628\u0627\u0642\u0629 2\)|\(\u0627\u0644\u0628\u0627\u0642\u0629 3\))/g);
  return parts.map((part, idx) => {
    if (part === '(Package 2)' || part === '(\u0627\u0644\u0628\u0627\u0642\u0629 2)') {
      return (
        <Link key={`pkg2-${idx}`} to="/dashboard/packages#package2" className="underline font-medium">
          {part}
        </Link>
      );
    }
    if (part === '(Package 3)' || part === '(\u0627\u0644\u0628\u0627\u0642\u0629 3)') {
      return (
        <Link key={`pkg3-${idx}`} to="/dashboard/packages#package3" className="underline font-medium">
          {part}
        </Link>
      );
    }
    return <React.Fragment key={`txt-${idx}`}>{part}</React.Fragment>;
  });
};

export default function PackageFeatureGate({
  featureName,
  currentPackageCode,
  onUpgrade,
  availabilityText,
  canUpgrade = true,
  contactAdminText,
}: Props) {
  const { t } = useI18n();

  const currentLabel = packageLabel(currentPackageCode, t);
  const currentPrice = packagePricing(currentPackageCode, t);
  const normalizedAvailability =
    availabilityText === 'This feature is available on Elite (Package 3) only.'
      ? t('pkg_gate_elite_only', 'This feature is available on Elite (Package 3) only.')
      : availabilityText;
  const highlights = currentPlanHighlights(currentPackageCode, t);
  const resolvedAvailability =
    normalizedAvailability || t('pkg_gate_available_on', 'This feature is available on Pro (Package 2) and Elite (Package 3).');
  const resolvedContactText =
    contactAdminText ||
    t('pkg_gate_contact_admin', 'Please contact your club admin to upgrade the package and unlock this feature.');

  return (
    <div className="w-full max-w-xl rounded-3xl border border-border bg-card text-card-foreground shadow-[0_20px_60px_-15px_rgba(0,0,0,0.4)] transition-all duration-300 hover:shadow-[0_25px_80px_-15px_rgba(0,0,0,0.55)]">
      <div className="p-7 sm:p-8 space-y-6">
        <div className="space-y-2">
          <p className="text-xs font-bold tracking-wider uppercase text-muted-foreground">
            {t('pkg_gate_upgrade_plan', 'Upgrade your plan')}
          </p>

          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
            {t('pkg_gate_unlock', 'Unlock')} <span className="text-primary">{featureName}</span>
          </h2>

          <p className="text-sm text-muted-foreground">{t('pkg_gate_unavailable', "This feature isn't available in your current plan.")}</p>
        </div>

        <div className="rounded-2xl border border-border bg-muted/40 px-5 py-4 backdrop-blur-sm">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('pkg_gate_current_plan', 'Your Current Plan')}</p>
          <p className="mt-1 text-lg font-semibold text-primary">{currentLabel}</p>
          <p className="mt-1 text-sm font-medium text-foreground">{currentPrice}</p>
        </div>

        <div className="space-y-3">
          {highlights.map((item) => (
            <div key={item} className="flex items-start gap-3 rounded-lg px-3 py-1 hover:bg-muted/40 transition">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10">
                <Check size={14} className="text-primary" />
              </div>

              <span className="text-sm leading-relaxed">{item}</span>
            </div>
          ))}

          <p className="text-sm text-muted-foreground pt-1">{renderAvailabilityWithLinks(resolvedAvailability)}</p>
        </div>

        {canUpgrade && onUpgrade ? (
          <Button
            onClick={onUpgrade}
            className="w-full h-11 text-base font-medium rounded-xl shadow-md transition-all duration-200 hover:scale-[1.02] hover:shadow-lg"
          >
            <Sparkles size={16} className="mr-2" />
            {t('pkg_gate_upgrade_button', 'Upgrade Package')}
          </Button>
        ) : (
          <div className="w-full rounded-xl border border-border bg-background px-4 py-3 text-sm text-muted-foreground text-center">
            {resolvedContactText}
          </div>
        )}
      </div>
    </div>
  );
}
